<template>
    <div
        class="compare-vehicle-info tw-p-4 lg:tw-py-10 tw-flex tw-flex-col tw-bg-white tw-relative lg:tw-w-full lg:tw-items-center lg:tw-flex-row lg:tw-gap-4"
        @click="onVehicleInfoClick"
    >
        <section class="compare-vehicle-info__img-container max-lg:tw-order-2 tw-max-w-full">
            <component
                :is="hasUrl ? 'a' : 'span'"
                v-bind="
                    hasUrl
                        ? {
                            href: vehicle.canonicalUrl,
                            target: '_blank',
                        }
                        : null
                "
                class="tw-relative tw-block tw-max-w-full"
            >
                <div v-if="vehicle.isElectric" class="tw-absolute tw-right-0 tw-top-0">
                    <dk-icon name="ds-bolt-filled" class="lg:!tw-hidden" color="brand-primary" size="s" />
                    <dk-icon name="ds-bolt-filled" class="max-lg:!tw-hidden" color="brand-primary" size="m" />
                </div>
                <img
                    :alt="imgAlt"
                    :src="vehicle.icons && getPrismImageUrl(vehicle.icons.icon, 280)"
                    loading="lazy"
                    class="tw-max-w-full lg:tw-max-w-[200px] tw-h-auto tw-block"
                    @click="trackVehicleImageClick"
                >
            </component>
        </section>
        <section class="compare-vehicle-info__container tw-overflow-hidden">
            <h2
                class="compare-vehicle-info__make tw-overflow-ellipsis tw-overflow-hidden tw-whitespace-normal caption lg:!tw-text-base tw-text-base-subtle tw-uppercase tw-leading-xs"
            >
                {{ vehicle.makeName }}
            </h2>
            <h3
                class="compare-vehicle-info__model subtitle-md tw-text-basic tw-font-semibold tw-overflow-ellipsis tw-overflow-hidden tw-whitespace-normal tw-leading-base"
            >
                {{ vehicle.submodelCommercialName }}
            </h3>
            <h4
                v-if="vehicle.versionName"
                class="compare-vehicle-info__version lg:tw-mt-6 tw-text-base-subtle micro tw-overflow-ellipsis tw-overflow-hidden tw-whitespace-normal tw-leading-2xs"
            >
                {{ vehicle.versionName }}
            </h4>
            <dk-chip
                v-if="vehicle.ecoLabel"
                :item="vehicle.ecoLabel"
                class="compare-vehicle-info__eco-label m-top--xxs"
            />
        </section>
        <section v-if="showMenu" class="compare-vehicle-info__menu tw-absolute tw-top-2 tw-right-1">
            <compare-vehicle-menu
                :can-request-quote="canRequestQuote"
                :url="vehicle.canonicalUrl"
                :configurator-url="vehicle.configuratorUrl"
                :disabled="disabled"
                @selection="onSelection"
            />
        </section>
    </div>
</template>

<script>
import { getPrismImageUrl } from '@v4/common/utils/image';
import gaEventHandler from '@v4/common/components/mixins/google-analytics';
import DkChip from '@v4/common/components/atoms/DkChip.vue';

/**
 * Compare vehicle info component
 *
 * @version 4.0.0
 * @example ./../examples/CompareVehicleInfo.md
 */
export default {
    name: 'CompareVehicleInfo',
    components: {
        DkChip,
        DkIcon: () => ({
            component: import(/* webpackChunkName: "DkIcon-vue" */ '@v4/common/components/atoms/DkIcon.vue'),
        }),
        CompareVehicleMenu: () => ({
            component: import(
                /* webpackChunkName: "CompareVehicleMenu-vue" */ '@v4/compare/components/atoms/CompareVehicleMenu.vue'
            ),
        }),
    },
    mixins: [gaEventHandler],
    props: {
        /**
         * Vehicle item
         */
        vehicle: {
            type: Object,
            required: true,
            default: () => {},
        },
        /**
         * Whether or not disabled
         */
        disabled: {
            type: Boolean,
            default: false,
        },
        /**
         * Wheter or not to show menu
         */
        showMenu: {
            type: Boolean,
            default: true,
        },
        /**
         * Available offers of the vehicle
         */
        offers: {
            type: Array,
            required: false,
            default: () => [],
        },
    },
    computed: {
        imgAlt() {
            let modelName = this.vehicle.submodelCommercialName;

            if (this.vehicle.versionName) {
                modelName = `${modelName} - ${this.vehicle.versionName}`;
            }

            return `${this.vehicle.makeName} ${modelName}`;
        },
        hasUrl() {
            return !!this.vehicle.canonicalUrl;
        },
        canRequestQuote() {
            if (!this.offers || !this.offers.length) {
                return false;
            }

            const activeOffers = this.offers.filter(
                (offer) =>
                    offer.type.slug.toLowerCase() !== 'listing' &&
                    offer.empty !== true &&
                    (offer.finalPrice || offer.originalPrice)
            );

            return activeOffers.length > 0;
        },
    },
    methods: {
        getPrismImageUrl,
        onSelection(item) {
            this.pushGaEvent('Compare page', 'click', `vehicle menu dropdown click to ${item.action}`);

            this.$emit('selection', {
                ...item,
                vehicle: this.vehicle,
            });
        },
        trackVehicleImageClick() {
            this.pushGaEvent('Compare page', 'click', 'vehicle image click to go to VDP page');
        },
        onVehicleInfoClick() {
            this.$emit('click', this.vehicle.submodelId);
        },
    },
};
</script>
